import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { RefreshCw, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { getAIStatus } from "../../api/ginoxAiApi";

const GinoxAIStatus = () => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [providers, setProviders] = useState(null);

  const fetchStatus = async (refresh = false) => {
    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const res = await getAIStatus(refresh);
      setProviders(res.data?.providers || {});
      if (refresh) {
        toast.success("Status refreshed successfully (cache bypassed)");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch AI status");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckCircle className="text-emerald-400 w-5 h-5" />;
      case "inactive":
        return <XCircle className="text-red-400 w-5 h-5" />;
      default:
        return <AlertTriangle className="text-yellow-400 w-5 h-5" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20";
      case "inactive":
        return "bg-red-500/15 text-red-400 border border-red-500/20";
      default:
        return "bg-yellow-500/15 text-yellow-400 border border-yellow-500/20";
    }
  };

  const getCardBorder = (status) => {
    switch (status) {
      case "active":
        return "border-emerald-500/30";
      case "inactive":
        return "border-red-500/30";
      default:
        return "border-yellow-500/30";
    }
  };

  const providerNames = {
    openai: "OpenAI",
    anthropic: "Anthropic",
    google: "Google AI",
    xai: "xAI (Grok)",
    exa: "Exa",
    lumaai: "Luma AI",
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-1">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Services
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
            Ginox AI <span className="text-gradient-ginox">Status</span>
          </h1>
        </div>
        <button
          onClick={() => fetchStatus(true)}
          disabled={refreshing}
          className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 flex items-center gap-2 self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh Status
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Monitor the status of all AI provider API keys. Click "Refresh Status" to bypass cache and get fresh data.
      </p>

      {loading ? (
        <div className="text-center text-sm text-muted-foreground py-12">Loading...</div>
      ) : !providers ? (
        <p className="text-sm text-muted-foreground">No provider data available</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(providers).map(([key, provider]) => (
            <div
              key={key}
              className={`rounded-lg border bg-card p-5 overflow-hidden ${getCardBorder(provider.status)}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {providerNames[key] || key}
                </h3>
                {getStatusIcon(provider.status)}
              </div>

              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Status</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(provider.status)}`}>
                    {provider.status?.toUpperCase() || "UNKNOWN"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Configured</span>
                  <span className="text-xs text-foreground font-mono">
                    {provider.configured ? "Yes" : "No"}
                  </span>
                </div>
                {provider.details && (
                  <div className="pt-2 border-t border-border/40">
                    <p className="text-xs text-muted-foreground break-words">
                      {provider.details}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GinoxAIStatus;
